/**
 * CR-05 offers and CR-06 the participation workspace (FR-PAR-002, FR-PAR-005,
 * FR-PAR-006, FR-PAR-007).
 *
 * The list is a stack of horizontal ribbons. View opens a dedicated page
 * with the campaign's complete information. The terms shown there are the
 * ones frozen onto the participation at acceptance (FR-PAR-006).
 */
import { useCallback, useEffect, useState } from "react";
import { Button, Eyebrow, LinkButton, PageHeader, StateBlock, Badge } from "@ui/primitives";
import { TextInput } from "@ui/forms";
import { useToast } from "@app/shared/Toasts";
import { formatMoney, fromMinorString } from "@contracts/money";
import { fontSize, space } from "@design/index";
import { api, type ApiResult } from "@app/shared/api";
import { RibbonSkeleton } from "@app/shared/Skeletons";
import { CampaignCover } from "@app/shared/CampaignTile";
import { PlatformRow, platformMeta } from "@app/shared/Platforms";
import {
  creatorPlatformRates, lowestPlatformRate, PlatformRateTiles, type StoredPlatformRate,
} from "@app/shared/platformRates";
import { campaignCoverUrl } from "@app/shared/coverUrl";
import {
  missingRequiredPlatforms, platformSubmitStatuses, type ConnectedAccount,
} from "@server/submissionOwnership";
import { SubmitPostDialog, SubmissionsScreen } from "./Submissions";
import {
  AssetBoard, BriefColumn, CampaignDetailSkeleton, CampaignSectionTabs, CampaignShowcase,
  ScheduleBoard, type CampaignSectionId,
} from "@app/shared/CampaignShowcase";
interface AcceptedTerms {
  cpm_gross_minor?: number;
  per_creator_cap_minor?: number;
  per_post_cap_minor?: number | null;
  submissions_per_creator?: number;
  submission_deadline_at?: string | null;
  campaign_ends_at?: string | null;
  tracking_days?: number;
  min_live_days?: number;
  earning_hold_days?: number;
  version?: number;
}

export interface ParticipationRow {
  id: string;
  campaign_id: string;
  state: string;
  origin: string;
  gross_cap_minor: string;
  currency: string;
  offered_at: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  accepted_terms: AcceptedTerms | null;
  display_name: string;
  brand_name: string;
  submission_deadline_at: string | null;
  campaign_ends_at?: string | null;
  join_opens_at?: string | null;
  cpm_gross_minor: string | null;
  per_creator_cap_minor?: string | null;
  submissions_per_creator?: number | null;
  measurement_routes?: string[] | null;
  cover_asset_id?: string | null;
  platform_rates?: StoredPlatformRate[] | null;
}

interface CampaignDetail {
  campaign_id: string;
  display_name: string;
  brand_name: string;
  brand_website: string | null;
  summary: string;
  objective: string | null;
  content_category: string | null;
  campaign_type: string | null;
  brief: Record<string, unknown>;
  eligibility: Record<string, unknown>;
  mode: string;
  currency: string;
  cpm_gross_minor: string;
  per_creator_cap_minor: string;
  per_post_cap_minor: string | null;
  submissions_per_creator: number;
  display_timezone: string;
  join_opens_at?: string | null;
  join_closes_at: string | null;
  submission_deadline_at: string | null;
  campaign_ends_at?: string | null;
  tracking_days: number;
  measurement_cutoff_at: string | null;
  min_live_days: number;
  earning_hold_days: number;
  measurement_routes: string[];
  remaining_capacity_minor: string;
  capacity_is_indicative: boolean;
  version: number;
  assets: { slot: string; kind: string; filename: string }[];
  cover_asset_id?: string | null;
  platform_rates?: StoredPlatformRate[] | null;
}

const messageOf = (r: Extract<ApiResult<unknown>, { ok: false }>) => r.message;
const minor = (v: string | number, ccy: string) =>
  fromMinorString(v, ccy as "GBP" | "USD" | "EUR");

const asDate = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleDateString(undefined,
    { day: "numeric", month: "short", year: "numeric" }) : "-";

function daysLeft(iso: string | null | undefined): string {
  if (!iso) return "no deadline set";
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "closed";
  if (days === 0) return "closes today";
  return days === 1 ? "1 day left" : `${days} days left`;
}

function hoursLeft(iso: string | null): string {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const hours = Math.ceil(ms / 3_600_000);
  return hours < 48 ? `${hours} hour${hours === 1 ? "" : "s"} left` : `${Math.ceil(hours / 24)} days left`;
}

const TONE: Record<string, "ok" | "warn" | "neutral" | "danger"> = {
  LIVE: "ok", OFFERED: "warn", COMPLETED: "neutral",
  DECLINED: "neutral", EXPIRED: "neutral", WITHDRAWN: "danger",
};

function stateLabel(state: string): string {
  return state.replaceAll("_", " ").toLowerCase();
}

export function WorkScreen({ onOpenCampaign }: { onOpenCampaign: (id: string) => void }) {
  const [rows, setRows] = useState<ParticipationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();

  const load = useCallback(async () => {
    const r = await api.get<{ participations: ParticipationRow[] }>("/participations");
    if (r.ok) setRows(r.data.participations); else setError(messageOf(r));
  }, []);

  useEffect(() => { void load(); }, [load]);

  const act = useCallback(async (id: string, path: string, body?: unknown, ok?: string) => {
    setBusy(id);
    const r = await api.post<Record<string, unknown>>(`/participations/${id}/${path}`, body);
    setBusy(null);
    if (r.ok) toast.ok(ok ?? "Done.");
    else toast.error(messageOf(r));
    await load();
  }, [load, toast]);

  if (error) return <StateBlock kind="error" title="Could not load your campaigns" detail={error} />;
  if (rows === null) return <RibbonSkeleton count={5} />;

  const offers = rows.filter((p) => p.state === "OFFERED");
  const live = rows.filter((p) => p.state === "LIVE");
  const past = rows.filter((p) => !["OFFERED", "LIVE"].includes(p.state));

  return (
    <>
      <PageHeader
        eyebrow="My campaigns"
        title="Campaigns and offers"
        description="Offers waiting on you, the campaigns you have joined, and everything finished."
      />

      {rows.length === 0 ? (
        <StateBlock
          kind="empty"
          title="You have not joined a campaign yet"
          detail="Open Home to browse what is currently accepting creators."
          action={<LinkButton href="/" variant="primary" size="sm">Browse campaigns</LinkButton>}
        />
      ) : null}

      {offers.length > 0 ? (
        <section style={{ display: "grid", gap: 10 }}>
          <Eyebrow>Waiting on you</Eyebrow>
          {offers.map((p) => (
            <WorkRibbon
              key={p.id}
              p={p}
              busy={busy === p.id}
              onView={() => onOpenCampaign(p.campaign_id)}
              onAccept={() => void act(p.id, "accept", undefined, "Offer accepted. The terms are now fixed.")}
              onDecline={() => void act(p.id, "decline", undefined, "Offer declined. The budget has been released.")}
            />
          ))}
        </section>
      ) : null}

      {live.length > 0 ? (
        <section style={{ display: "grid", gap: 10, marginTop: offers.length > 0 ? space(5) : 0 }}>
          <Eyebrow>Active</Eyebrow>
          {live.map((p) => (
            <WorkRibbon
              key={p.id}
              p={p}
              busy={busy === p.id}
              onView={() => onOpenCampaign(p.campaign_id)}
            />
          ))}
        </section>
      ) : null}

      {past.length > 0 ? (
        <section style={{ display: "grid", gap: 10, marginTop: (offers.length + live.length) > 0 ? space(5) : 0 }}>
          <Eyebrow>Finished</Eyebrow>
          {past.map((p) => (
            <WorkRibbon
              key={p.id}
              p={p}
              busy={false}
              onView={() => onOpenCampaign(p.campaign_id)}
            />
          ))}
        </section>
      ) : null}
    </>
  );
}

function RibbonFact({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "grid", gap: 3, minWidth: 0 }}>
      <span style={{ fontSize: 11, color: "var(--faint)", fontWeight: 600, letterSpacing: "0.02em" }}>
        {label}
      </span>
      <strong style={{
        fontSize: fontSize.bodyLg, fontWeight: 650, letterSpacing: "-0.01em",
        fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>
        {value}
      </strong>
    </div>
  );
}

function WorkRibbon({
  p, busy, onView, onAccept, onDecline,
}: {
  p: ParticipationRow;
  busy: boolean;
  onView: () => void;
  onAccept?: () => void;
  onDecline?: () => void;
}) {
  const offer = p.state === "OFFERED";
  const terms = p.accepted_terms ?? {};
  const rate = p.cpm_gross_minor ?? (terms.cpm_gross_minor != null ? String(terms.cpm_gross_minor) : null);
  const rates = creatorPlatformRates({
    routes: p.measurement_routes,
    rates: p.platform_rates,
    fallbackCpm: rate,
  });
  const lowest = rates.length > 1 ? lowestPlatformRate(rates) : null;
  const posts = p.submissions_per_creator ?? terms.submissions_per_creator;
  const deadline = p.submission_deadline_at ?? terms.submission_deadline_at;
  const ends = p.campaign_ends_at ?? terms.campaign_ends_at;
  const whenLabel = offer ? "Expires" : ends ? "Ends" : p.state === "LIVE" ? "Joined" : "Date";
  const whenValue = offer
    ? (hoursLeft(p.expires_at) || asDate(p.expires_at))
    : (ends ? asDate(ends) : asDate(p.accepted_at ?? p.offered_at));

  return (
    <div
      className="cm-work-ribbon cm-campaign-ribbon"
      style={{
        display: "grid",
        gridTemplateColumns: "54px minmax(160px, 1.2fr) minmax(280px, 3fr) auto auto",
        alignItems: "center",
        gap: 16,
        padding: "12px 16px",
        border: "1px solid var(--line)",
        borderRadius: 14,
        background: "var(--surface)",
      }}
    >
      <figure style={{
        position: "relative", margin: 0, width: 54, aspectRatio: "3 / 4",
        borderRadius: 10, overflow: "hidden", border: "1px solid var(--line)", flex: "0 0 54px",
      }}>
        {p.cover_asset_id ? (
          <img
            data-tile-media=""
            src={campaignCoverUrl(p.campaign_id, p.cover_asset_id)}
            alt=""
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <CampaignCover brand={p.brand_name} name={p.display_name} />
        )}
      </figure>
      <div style={{ minWidth: 0, display: "grid", gap: 3 }}>
        <strong style={{
          fontSize: fontSize.bodyLg, fontWeight: 650, letterSpacing: "-0.01em",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>
          {p.display_name}
        </strong>
        <span style={{ fontSize: fontSize.label, color: "var(--muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {p.brand_name}
        </span>
        <PlatformRow routes={p.measurement_routes} size={16} />
      </div>
      <div className="cm-work-ribbon-facts" style={{
        display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 12, minWidth: 0,
      }}>
        <RibbonFact
          label="Rate"
          value={lowest
            ? `From ${formatMoney(minor(lowest, p.currency))} / 1K`
            : rate ? `${formatMoney(minor(rate, p.currency))} / 1K` : "-"}
        />
        <RibbonFact
          label="Cap"
          value={formatMoney(minor(p.gross_cap_minor, p.currency))}
        />
        <RibbonFact
          label="Submit by"
          value={deadline ? asDate(deadline) : (posts ? `Up to ${posts} posts` : "-")}
        />
        <RibbonFact label={whenLabel} value={whenValue} />
      </div>
      <div className="cm-work-ribbon-end">
        <Badge tone={TONE[p.state] ?? "neutral"}>
          {offer ? (p.origin === "INVITATION" ? "Invitation" : "Offer") : stateLabel(p.state)}
        </Badge>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap" }}>
          {onAccept ? (
            <Button size="sm" onClick={onAccept} disabled={busy}>
              {busy ? "Working…" : "Accept"}
            </Button>
          ) : null}
          {onDecline ? (
            <Button size="sm" variant="secondary" onClick={onDecline} disabled={busy}>
              Decline
            </Button>
          ) : null}
          <Button size="sm" variant="primary" onClick={onView}>View</Button>
        </div>
      </div>
    </div>
  );
}

function readable(text: string | null | undefined): string | null {
  if (!text) return null;
  const t = text.trim();
  return t.length >= 8 ? t : null;
}

function parseCampaignPageTab(value: string | null | undefined): CampaignSectionId {
  if (value === "submissions" || value === "brief") return value;
  return "general";
}

export function WorkDetailScreen({
  id, onBack, tab, onTab, onOpenSubmission,
}: {
  id: string;
  onBack: () => void;
  tab?: string | null;
  onTab?: (tab: CampaignSectionId) => void;
  onOpenSubmission?: (id: string) => void;
}) {
  const [campaign, setCampaign] = useState<CampaignDetail | null>(null);
  const [participation, setParticipation] = useState<ParticipationRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const [submitOpen, setSubmitOpen] = useState(false);
  const [connections, setConnections] = useState<ConnectedAccount[]>([]);
  const [usedPosts, setUsedPosts] = useState<Array<{ platform?: string; state: string }>>([]);
  const [listTick, setListTick] = useState(0);

  const load = useCallback(async () => {
    setError(null);
    const [c, p, s, subs] = await Promise.all([
      api.get<{ campaign: CampaignDetail }>(`/campaigns/${id}`),
      api.get<{ participations: ParticipationRow[] }>("/participations"),
      api.get<{ connections: ConnectedAccount[] }>("/social-connections"),
      api.get<{ submissions: Array<{ campaign_id: string; platform?: string; state: string }> }>("/submissions"),
    ]);
    if (!c.ok) { setError(messageOf(c)); return; }
    setCampaign(c.data.campaign);
    const match = p.ok
      ? (p.data.participations ?? []).find((row) => row.campaign_id === id) ?? null
      : null;
    setParticipation(match);
    setConnections(s.ok ? (s.data.connections ?? []) : []);
    const mine = subs.ok ? (subs.data.submissions ?? []) : [];
    setUsedPosts(mine.filter((row) => row.campaign_id === id && row.state !== "WITHDRAWN"));
    setListTick((n) => n + 1);
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  const act = useCallback(async (path: string, body?: unknown, ok?: string) => {
    if (!participation) return;
    setBusy(true);
    const r = await api.post<Record<string, unknown>>(
      `/participations/${participation.id}/${path}`, body);
    setBusy(false);
    if (r.ok) toast.ok(ok ?? "Done.");
    else toast.error(messageOf(r));
    await load();
  }, [participation, load, toast]);

  if (error) {
    return (
      <>
        <StateBlock kind="error" title="Could not load this campaign" detail={error} />
        <div style={{ marginTop: space(3) }}>
          <Button variant="secondary" onClick={onBack}>Back to my campaigns</Button>
        </div>
      </>
    );
  }
  if (!campaign) return <CampaignDetailSkeleton />;

  const c = campaign;
  const ccy = c.currency;
  const terms = participation?.accepted_terms ?? {};
  const rateMinor = c.cpm_gross_minor ?? participation?.cpm_gross_minor ?? terms.cpm_gross_minor;
  const rates = creatorPlatformRates({
    routes: c.measurement_routes,
    rates: c.platform_rates ?? participation?.platform_rates,
    fallbackCpm: rateMinor,
  });
  const capMinor = c.per_creator_cap_minor ?? participation?.gross_cap_minor;
  const deadline = c.submission_deadline_at ?? participation?.submission_deadline_at ?? terms.submission_deadline_at;
  const opens = c.join_opens_at ?? null;
  const ends = c.campaign_ends_at ?? terms.campaign_ends_at ?? c.measurement_cutoff_at;
  const posts = c.submissions_per_creator;
  const summary = readable(c.summary);
  const objective = readable(c.objective);
  const live = participation?.state === "LIVE";
  const offered = participation?.state === "OFFERED";
  const requiredPlatforms = c.measurement_routes;
  const missingPlatforms = missingRequiredPlatforms(requiredPlatforms, connections);
  const slots = platformSubmitStatuses({
    required: requiredPlatforms,
    connections,
    submissions: usedPosts,
    limit: posts,
  });
  const submittedCount = slots.reduce((n, s) => n + s.submitted, 0);
  const slotLimit = slots.length ? slots.length * posts : posts;
  const atCap = slots.length
    ? slots.every((s) => s.submitted >= s.limit)
    : submittedCount >= posts;
  const canSubmit = live && (slots.length ? slots.some((s) => s.canSubmit) : !atCap);
  const postsHint = slots.length > 1
    ? `One link per required platform`
    : posts === 1 ? "One post allowed" : `Up to ${posts} posts`;
  const usedHint = atCap
    ? slots.length > 1
      ? "You have submitted every required platform"
      : `You have used your ${posts} post${posts === 1 ? "" : "s"}`
    : slots.length > 1
      ? `${submittedCount} of ${slotLimit} platform links sent`
      : `${submittedCount} of ${posts} used`;

  const activeTab = parseCampaignPageTab(tab);
  const submissionCount = usedPosts.length;

  const schedule: { label: string; value: string }[] = [
    participation?.accepted_at ? { label: "Joined", value: asDate(participation.accepted_at) } : null,
    offered && participation?.expires_at
      ? { label: "Offer expires", value: `${asDate(participation.expires_at)} · ${hoursLeft(participation.expires_at)}` }
      : null,
    opens ? { label: "Opening date", value: asDate(opens) } : null,
    { label: "Content submission", value: asDate(deadline) },
    ends ? { label: "Campaign end", value: asDate(ends) } : null,
    { label: "Stay live", value: `${c.min_live_days} days` },
    { label: "Tracked for", value: `${c.tracking_days} days per post` },
    { label: "Earnings held", value: `${c.earning_hold_days} days after release` },
  ].filter((row): row is { label: string; value: string } => row !== null);

  return (
    <div style={{ display: "grid", gap: 20 }}>
      <a
        href="/work"
        onClick={(e) => { e.preventDefault(); onBack(); }}
        style={{
          fontSize: fontSize.bodyLg, color: "var(--link)", textDecoration: "none",
          fontWeight: 600, display: "inline-block",
        }}
      >
        ← My campaigns
      </a>

      <CampaignSectionTabs
        value={activeTab}
        onChange={(next) => onTab?.(next)}
        includeSubmissions
        submissionCount={submissionCount}
      />

      {activeTab === "submissions" ? (
        <div id="campaign-panel-submissions" role="tabpanel" aria-labelledby="campaign-tab-submissions">
          <SubmissionsScreen
            campaignId={id}
            embedded
            reloadKey={listTick}
            onOpen={(sid) => onOpenSubmission?.(sid)}
            emptyAction={live ? (
              <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)" }}>
                Open General and use Submit beside the campaign title.
              </p>
            ) : undefined}
          />
        </div>
      ) : activeTab === "brief" ? (
        <div id="campaign-panel-brief" role="tabpanel" aria-labelledby="campaign-tab-brief">
          <BriefColumn
            brief={c.brief}
            emptyAction={live ? (
              <div style={{
                display: "grid", gap: 10, padding: 16, borderRadius: 12,
                background: "var(--surface-2)", border: "1px solid var(--line)",
              }}>
                <strong style={{ fontSize: fontSize.bodyLg }}>Next step</strong>
                <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.55 }}>
                  {atCap
                    ? usedHint + ". This campaign does not accept another post from you."
                    : canSubmit
                      ? "Send each required platform as its own link. A connected account is enough - you do not need the others first."
                      : `Connect ${missingPlatforms.map((pid) => platformMeta(pid).label).join(" and ")} first. We only accept a post from an account you have already connected.`}
                </p>
                <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)" }}>
                  Open General and use Submit beside the campaign title.
                </p>
              </div>
            ) : null}
          />
        </div>
      ) : (
      <div id="campaign-panel-general" role="tabpanel" aria-labelledby="campaign-tab-general" style={{ display: "grid", gap: 22 }}>
      <CampaignShowcase
        showBack={false}
        backHref="/work"
        backLabel="My campaigns"
        onBack={onBack}
        brand={c.brand_name}
        name={c.display_name}
        summary={summary ?? objective}
        badge={participation ? stateLabel(participation.state) : null}
        coverUrl={c.cover_asset_id ? campaignCoverUrl(c.campaign_id, c.cover_asset_id) : null}
        stats={[
          { label: "Cap", value: formatMoney(minor(c.per_creator_cap_minor ?? capMinor, ccy)), hint: slots.length > 1 ? "Maximum on each required platform" : "Maximum on this campaign" },
          { label: "Submit by", value: asDate(deadline), hint: daysLeft(deadline) },
          { label: "Ends", value: asDate(ends), hint: ends ? "Review and earnings start" : "No end date set" },
          { label: "Posts", value: `${submittedCount} / ${slotLimit}`, hint: atCap ? usedHint : postsHint },
        ]}
        afterStats={rates.length > 0 ? <PlatformRateTiles rows={rates} currency={ccy} /> : null}
        actions={
          live ? (
            <Button variant="primary" onClick={() => setSubmitOpen(true)}>Submit</Button>
          ) : offered ? (
            <>
              <Button
                variant="primary"
                onClick={() => void act("accept", undefined, "Offer accepted. The terms are now fixed.")}
                disabled={busy}
              >
                {busy ? "Working…" : "Accept offer"}
              </Button>
              <Button
                variant="secondary"
                onClick={() => void act("decline", undefined, "Offer declined. The budget has been released.")}
                disabled={busy}
              >
                Decline
              </Button>
            </>
          ) : null
        }
        afterSummary={
          <>
            {live && atCap ? (
              <span style={{ fontSize: fontSize.body, color: "var(--muted)", fontWeight: 650 }}>
                {usedHint}
              </span>
            ) : null}
            {c.brand_website ? (
              <a href={c.brand_website} target="_blank" rel="noreferrer"
                style={{ fontSize: fontSize.bodyLg, fontWeight: 650 }}>
                Brand site
              </a>
            ) : null}
          </>
        }
      />
      <div className="cm-general-stack">
        <ScheduleBoard
          rows={schedule}
          info={`Dates in ${c.display_timezone}.${live ? " These match the live campaign brief." : ""}`}
        />
        <AssetBoard assets={c.assets} />
        {live ? (
          <section className="cm-leave-card">
            {withdrawing ? (
              <div style={{ display: "grid", gap: 12, width: "100%" }}>
                <strong style={{ fontSize: fontSize.bodyLg }}>Need to leave?</strong>
                <TextInput
                  label="Why are you withdrawing?"
                  value={reason}
                  onChange={(e) => setReason(e.currentTarget.value)}
                  hint="Once you have submitted work, withdrawing starts a review rather than cancelling it."
                  required
                />
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  <Button
                    variant="danger"
                    disabled={busy || reason.trim().length < 3}
                    onClick={() => void act("withdraw", { reason }, "You have withdrawn from this campaign.")}
                  >
                    {busy ? "Working…" : "Confirm withdrawal"}
                  </Button>
                  <Button variant="ghost" onClick={() => setWithdrawing(false)}>Cancel</Button>
                </div>
              </div>
            ) : (
              <>
                <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
                  <strong style={{ fontSize: fontSize.bodyLg }}>Need to leave?</strong>
                  <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.55 }}>
                    Withdraw only if you cannot deliver. The campaign keeps the reserved budget.
                  </p>
                </div>
                <Button variant="ghost" onClick={() => setWithdrawing(true)}>Withdraw</Button>
              </>
            )}
          </section>
        ) : null}
      </div>
      </div>
      )}
      {live && participation ? (
        <SubmitPostDialog
          open={submitOpen}
          onClose={() => setSubmitOpen(false)}
          onCreated={() => { setSubmitOpen(false); void load(); }}
          participationId={participation.id}
          campaignName={c.display_name}
          requiredPlatforms={requiredPlatforms}
          connections={connections}
          submissions={usedPosts}
          submissionLimit={posts}
        />
      ) : null}
    </div>
  );
}
